/**
 * #65 — Vendor-facing Purchase Order review page.
 *
 * Public route — no auth required. The PO's `short_code` (from the
 * URL) is the bearer token; the backend's `/r/wpo/{code}` resolver
 * rate-limits resolution and this page hits two PUBLIC endpoints
 * (`/api/purchase-orders/by-short-code/{code}` and
 * `/api/purchase-orders/by-short-code/{code}/pdf`).
 *
 * Layout matches the WOReview / ProposalReview pattern: branded header,
 * header card with WO + jobsite info, full line-item table, and a
 * sticky "Download PDF" button.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Download, AlertTriangle, FileText, Building, MapPin } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const fmt = (n) => new Intl.NumberFormat("en-US", {
  style: "currency", currency: "USD", minimumFractionDigits: 2,
}).format(Number(n) || 0);

export default function POReview() {
  const { code } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API_BASE}/api/purchase-orders/by-short-code/${encodeURIComponent(code)}`);
        if (!cancelled) setState({ loading: false, error: null, data: r.data });
      } catch (e) {
        if (cancelled) return;
        const status = e?.response?.status;
        const msg = status === 404
          ? "This purchase order link is no longer valid or has been revoked."
          : "We couldn't load this purchase order. Please contact the buyer.";
        setState({ loading: false, error: msg, data: null });
      }
    };
    load();
    return () => { cancelled = true; };
  }, [code]);

  if (state.loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900" />
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md bg-white border border-amber-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-amber-500 shrink-0" />
            <div>
              <h1 className="text-lg font-semibold text-slate-800 mb-1">Purchase Order Unavailable</h1>
              <p className="text-sm text-slate-600">{state.error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { po, project_name, jobsite_address } = state.data || {};
  const lineItems = po?.line_items || [];
  const subtotal = lineItems.reduce((s, li) => s + (Number(li.total) || (Number(li.quantity) || 0) * (Number(li.unit_cost) || 0)), 0);
  const tax = Number(po?.tax) || 0;
  const total = Number(po?.total) || subtotal + tax;

  const pdfUrl = `${API_BASE}/api/purchase-orders/by-short-code/${encodeURIComponent(code)}/pdf?download=1`;

  return (
    <div className="min-h-screen bg-slate-50 pb-32" data-testid="po-review-page">
      <header className="bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white text-slate-900 font-bold flex items-center justify-center text-lg shadow">
              ABS
            </div>
            <div>
              <div className="text-base font-semibold">ABS Inc.</div>
              <div className="text-xs text-slate-300">Purchase Order</div>
            </div>
          </div>
          <a
            href={pdfUrl}
            className="hidden sm:inline-flex items-center gap-2 bg-white text-slate-900 px-3 py-2 rounded-lg text-sm font-semibold hover:bg-slate-100"
            data-testid="btn-download-po-pdf-header"
          >
            <Download className="w-4 h-4" /> Download PDF
          </a>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        {/* Header card */}
        <section className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="po-header-card">
          <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500">PO Number</div>
              <div className="text-2xl font-bold text-slate-900 font-mono" data-testid="po-number">
                {po?.po_number || "—"}
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-wider text-slate-500">Total</div>
              <div className="text-2xl font-bold text-emerald-700" data-testid="po-total">{fmt(total)}</div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1">
                <Building className="w-3 h-3" /> Vendor
              </div>
              <div className="font-semibold text-slate-800" data-testid="po-vendor-name">
                {po?.vendor_name || po?.vendor || "—"}
              </div>
              {po?.vendor_address && <div className="text-xs text-slate-500 mt-0.5 whitespace-pre-line">{po.vendor_address}</div>}
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1">
                <FileText className="w-3 h-3" /> Project
              </div>
              <div className="font-semibold text-slate-800" data-testid="po-project-name">
                {project_name || "—"}
              </div>
              {jobsite_address && (
                <div className="text-xs text-slate-500 mt-0.5 flex items-start gap-1">
                  <MapPin className="w-3 h-3 mt-0.5 shrink-0" />
                  <span>{jobsite_address}</span>
                </div>
              )}
            </div>
          </div>

          {po?.notes && (
            <div className="mt-4 pt-4 border-t border-slate-100">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Notes</div>
              <div className="text-sm text-slate-700 whitespace-pre-line">{po.notes}</div>
            </div>
          )}
        </section>

        {/* Line items */}
        <section className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="po-line-items">
          <div className="px-5 py-3 border-b border-slate-100 font-semibold text-sm text-slate-800">
            Line Items
          </div>
          {lineItems.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-400">No line items on this PO.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-2 w-[100px]">Part #</th>
                  <th className="px-4 py-2">Description</th>
                  <th className="px-4 py-2 text-right w-[70px]">Qty</th>
                  <th className="px-4 py-2 text-right w-[100px]">Unit</th>
                  <th className="px-4 py-2 text-right w-[120px]">Total</th>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((li, idx) => {
                  const lineTotal = Number(li.total) || (Number(li.quantity) || 0) * (Number(li.unit_cost) || 0);
                  return (
                    // eslint-disable-next-line react/no-array-index-key -- vendor review snapshot; no stable id
                    <tr key={`po-li-${idx}`} className="border-t border-slate-100" data-testid={`po-line-${idx}`}>
                      <td className="px-4 py-2 font-mono text-xs text-slate-600">{li.part_number || ""}</td>
                      <td className="px-4 py-2 text-slate-800">{li.description || li.name || "—"}</td>
                      <td className="px-4 py-2 text-right font-mono text-slate-600">{li.quantity || 1}</td>
                      <td className="px-4 py-2 text-right font-mono">{fmt(li.unit_cost)}</td>
                      <td className="px-4 py-2 text-right font-mono font-semibold">{fmt(lineTotal)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-slate-50 border-t border-slate-200">
                <tr>
                  <td colSpan={4} className="px-4 py-2 text-right text-xs uppercase tracking-wider text-slate-500">Subtotal</td>
                  <td className="px-4 py-2 text-right font-mono">{fmt(subtotal)}</td>
                </tr>
                {tax > 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-2 text-right text-xs uppercase tracking-wider text-slate-500">Tax</td>
                    <td className="px-4 py-2 text-right font-mono">{fmt(tax)}</td>
                  </tr>
                )}
                <tr>
                  <td colSpan={4} className="px-4 py-3 text-right text-sm font-semibold text-slate-800">Total</td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-base text-emerald-700">{fmt(total)}</td>
                </tr>
              </tfoot>
            </table>
          )}
        </section>
      </main>

      {/* Sticky mobile download button */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-3 sm:hidden shadow-lg">
        <a
          href={pdfUrl}
          className="flex items-center justify-center gap-2 w-full bg-slate-900 text-white py-3 rounded-lg font-semibold text-sm"
          data-testid="btn-download-po-pdf-mobile"
        >
          <Download className="w-4 h-4" /> Download PDF
        </a>
      </div>
    </div>
  );
}
